import React, { useEffect, useState } from "react";
import { Code2, Users, Target } from "lucide-react";
import ic_launcher from "../resources/ic_launcher.png";
import CompanyPhoto from "../resources/companyPhoto.png";
import CEO from "../resources/CEO.png";
import TC from "../resources/TC.png";
import CTO from "../resources/CTO.png";
import Card from "../components/Card";
import CardContent from "../components/CardContent";

const Home = () => {
  return (
    <section id="home" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <div className="mx-auto h-36 w-36 overflow-hidden rounded-full border-4 border-white bg-white shadow-xl ring-4 ring-emerald-100">
          <img
            src={ic_launcher}
            alt={"logo"}
            className="h-full w-full object-cover"
          />
        </div>

        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          Putting your business to flight...
        </h2>

        <p className="mt-4 text-slate-600">
          Phyxsius Solutions, Inc. is an enterprise solutions provider based in
          the Philippines, specializing in web and mobile application
          development. We are a well-established company offering outsourcing
          services to organizations seeking software development expertise.
        </p>
      </div>

      {/* Company Background */}
      <div className="mt-12 mb-10 grid gap-10 lg:grid-cols-2">
        <div>
          <h3 className="text-xl font-bold">Our Story</h3>

          <p className="mt-3 leading-7 text-slate-600">
            Founded in the second quarter of 2004, Phyxsius Solutions quickly
            engaged in the creation of a major banking application for one of
            the country’s top savings banks, as well as the development of a
            property management software for a prominent real estate developer
            in the Philippines. The company is 100% Filipino-owned and boasts a
            skilled and dedicated team capable of delivering world-class
            products and services.
          </p>
        </div>

        <div className="relative mx-auto w-full max-w-[520px]">
          <div className="absolute -left-6 -top-6 h-32 w-32 rounded-full bg-emerald-200/40 blur-3xl" />
          <div className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full bg-blue-200/40 blur-3xl" />

          <div className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl transition duration-500 hover:-translate-y-1 hover:shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#107b38]/10 via-transparent to-[#315ada]/10 opacity-0 transition duration-500 group-hover:opacity-100" />

            <div className="overflow-hidden rounded-[1.5rem]">
              <img
                src={CompanyPhoto}
                alt="Company"
                className="h-[300px] w-full object-cover transition duration-700 group-hover:scale-105"
              />
            </div>

            <div className="absolute left-6 top-6 rounded-2xl border border-white/30 bg-white/80 px-4 py-2 backdrop-blur-md shadow-lg">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Company Culture
              </p>

              <h4 className="text-sm font-bold text-slate-900">
                Innovation & Collaboration
              </h4>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">
          Company Foundation
        </p>

        <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Vision, Technology, and Team
        </h2>

        <p className="mt-4 text-lg leading-8 text-slate-600">
          Building scalable software solutions powered by modern technologies
          and experienced professionals.
        </p>
      </div>

      <div className="mt-14 grid gap-8 lg:grid-cols-3">
        <div className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-green-400 text-white shadow-md transition duration-300 group-hover:scale-110 group-hover:rotate-3">
            <Target size={26} />
          </div>

          <h3 className="mt-6 text-2xl font-bold text-slate-900">
            Vision & Strategy
          </h3>

          <p className="mt-4 leading-8 text-slate-600">
            Our goal is to offer clients highly efficient business solutions
            that enable them to build and maintain scalable applications.
            Through our expertise in technology implementation, we help
            organizations choose suitable platforms and architectures tailored
            to their business requirements.
          </p>
        </div>

        {/* Technology */}
        <div className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white shadow-md transition duration-300 group-hover:scale-110 group-hover:rotate-3">
            <Code2 size={26} />
          </div>

          <h3 className="mt-6 text-2xl font-bold text-slate-900">Technology</h3>

          <p className="mt-4 leading-8 text-slate-600">
            Phyxsius Solutions continuously invests in research and development
            to stay aligned with modern software technologies and scalable
            architectures.
          </p>

          {/* Technology Stack */}
          <div className="mt-6 flex flex-wrap gap-3">
            {[
              "VB.Net",
              "C#",
              "SQL Server",
              "ReactJS",
              "React Native",
              "Oracle Database",
              "Kafka",
            ].map((tech) => (
              <div
                key={tech}
                className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
              >
                {tech}
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-400 text-white shadow-md transition duration-300 group-hover:scale-110 group-hover:rotate-3">
            <Users size={26} />
          </div>

          <h3 className="mt-6 text-2xl font-bold text-slate-900">Team</h3>

          <p className="mt-4 leading-8 text-slate-600">
            Our team is composed of experienced professionals specializing in
            scalable web and mobile applications across industries such as
            banking, property management, and freight forwarding.
          </p>

          <div className="mt-5 rounded-2xl bg-violet-50 border border-violet-100 p-5">
            <p className="text-sm leading-7 text-violet-800">
              Led by industry professionals with over
              <span className="font-bold"> 30 years of experience</span>, the
              company also nurtures new graduates by providing hands-on
              training, mentorship, and exposure to innovative technologies.
            </p>
          </div>
        </div>
      </div>

      {/* Leadership */}
      <div className="mt-16">
        <h3 className="text-center text-2xl font-bold">Leadership Team</h3>

        <div className="mt-8 gap-6 sm:grid-cols-2 lg:grid-cols-2">
          {[
            {
              name: "Isagani B. Montenegro Jr.",
              pic: CEO,
              role: "Chief Executive Officer",
              text: "Mr. Isagani B. Montenegro Jr. has almost 4 decades of experience in the software development industry. He specializes in Systems Design and Analysis(SAD).He spends a lot of time understanding client business requirements and processes transforming it to system designs that are reviewed by and agreed on by the stakeholders and used by the company's software developers as guide in building client applications. Aside from designing systems and managing projects, he involves himself on actual software development writing codes on various applications across different industries. He designed and developed the Integrated Property Accounting System(IPAS), a product now being offered with customization by the company, way back 1999. This application is being used by more than 200 condominiums nationwide. He brings his experience and understanding of business processes from different industries with the objective of delivering dynamic, scaleable and responsive applications to the company's clients.",
            },
            {
              name: "Hermilindo G. Alcanites",
              pic: CTO,
              role: "Chief Technology Officer",
              text: "The Chief Technology Officer (CTO) of the company, Mr. Hermilindo G. Alcanites, had been with the company since day one performing top management decisions and herding the company with regards to technology implementation. He specializes on Research and Development(R&D) thru which the company helps its clients to decide which technologies to use and invest on, mostly being on web and mobile platforms. The CTO also conducts internal trainings to keep its Software Developers abreast with current development platforms that are offered to and/ or requested by the company's clients. With almost 4 decades of experience in the IT industry, he brings technology implementation knowledge from various industries.",
            },
          ].map((person) => (
            <Card
              key={person.name}
              className="mt-6 rounded-3xl border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
            >
              <CardContent className="p-7 text-center">
                <div className="mx-auto h-36 w-36 overflow-hidden rounded-full border-4 border-white bg-slate-100 shadow-xl ring-4 ring-emerald-100">
                  <img
                    src={person.pic}
                    alt={person.name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://via.placeholder.com/150?text=Photo";
                    }}
                  />
                </div>

                <h4 className="mt-4 font-bold">{person.name}</h4>

                <p className="text-sm text-slate-500">{person.role}</p>
                <p className="mt-4 leading-8 text-slate-600">{person.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Home;
